/**
 * Shared "카카오톡으로 공유" (③B, docs/OZK_FINAL_LAUNCH_STRATEGY.md 16장) helper.
 *
 * Kept separate from client/src/components/ShareKakaoButton.tsx so pages that
 * already own a single generic share icon/handler (e.g. BusinessDirectoryDetail's
 * BusinessProfileHero onShare) can try Kakao Talk's native share sheet first
 * and fall back to their existing native-share/clipboard logic, instead of
 * having two competing share buttons on the same page.
 */
import { initKakao } from "./firebase";

export interface KakaoShareContent {
  title: string;
  description?: string;
  imageUrl?: string;
  /** Relative or absolute path to the content being shared. */
  path: string;
  contentType: string;
  ctaLabel?: string;
}

/** Appends utm params so later link visits/signups can be attributed back to a ③B share. */
export function buildKakaoShareUrl(path: string, contentType: string): string {
  if (typeof window === "undefined") return path;
  const url = new URL(path, window.location.origin);
  url.searchParams.set("utm_source", "kakao_share");
  url.searchParams.set("utm_medium", "share");
  url.searchParams.set("utm_content", contentType);
  return url.toString();
}

/**
 * Attempts to open Kakao Talk's native share sheet via the Kakao JS SDK.
 * Returns true only if sendDefault was actually invoked - callers must still
 * fall back to navigator.share/clipboard when this returns false (no
 * VITE_KAKAO_JAVASCRIPT_KEY configured, SDK failed to load, or the "카카오톡
 * 공유"(Share) product isn't enabled for this Kakao app).
 */
export function tryOpenKakaoShareSheet(content: KakaoShareContent): boolean {
  if (typeof window === "undefined") return false;
  try {
    initKakao();
    if (!window.Kakao?.isInitialized?.() || !window.Kakao.Share?.sendDefault) {
      return false;
    }
    const shareUrl = buildKakaoShareUrl(content.path, content.contentType);
    window.Kakao.Share.sendDefault({
      objectType: "feed",
      content: {
        title: content.title,
        description: content.description || "",
        imageUrl: content.imageUrl || `${window.location.origin}/og-image.jpg`,
        link: { mobileWebUrl: shareUrl, webUrl: shareUrl },
      },
      buttons: [
        {
          title: content.ctaLabel || "자세히 보기",
          link: { mobileWebUrl: shareUrl, webUrl: shareUrl },
        },
      ],
    });
    return true;
  } catch (error) {
    console.error("[kakao-share] Kakao.Share.sendDefault failed:", error);
    return false;
  }
}
